import { useState, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { Camera, CameraResultType, CameraSource, Photo } from '@capacitor/camera';

interface UseNativeCameraOptions {
  quality?: number;
  maxWidth?: number;
  maxHeight?: number;
}

export const useNativeCamera = (options: UseNativeCameraOptions = {}) => {
  const { quality = 80, maxWidth = 1920, maxHeight = 1920 } = options;
  const [loading, setLoading] = useState(false);

  const isNative = Capacitor.isNativePlatform();

  const convertPhotoToFile = async (photo: Photo): Promise<File | null> => {
    try {
      if (!photo.base64String && !photo.webPath) {
        console.error('No image data available');
        return null;
      }

      let blob: Blob;

      if (photo.base64String) {
        // Convert base64 to blob
        const byteCharacters = atob(photo.base64String);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        blob = new Blob([byteArray], { type: `image/${photo.format || 'jpeg'}` });
      } else if (photo.webPath) {
        // Fetch from web path
        const response = await fetch(photo.webPath);
        blob = await response.blob();
      } else {
        return null;
      }

      const fileName = `photo_${Date.now()}.${photo.format || 'jpeg'}`;
      return new File([blob], fileName, { type: blob.type });
    } catch (error) {
      console.error('Error converting photo to file:', error);
      return null;
    }
  };

  const takePhoto = useCallback(async (): Promise<File | null> => {
    if (!isNative) {
      console.warn('takePhoto is only available on native platforms');
      return null;
    }

    setLoading(true);
    try {
      const photo = await Camera.getPhoto({
        quality,
        allowEditing: false,
        resultType: CameraResultType.Base64,
        source: CameraSource.Camera,
        width: maxWidth,
        height: maxHeight,
        correctOrientation: true,
      });

      return await convertPhotoToFile(photo);
    } catch (error: any) {
      // User cancelled or permission denied - don't throw (handles iOS and Android)
      if (error?.message?.includes('User cancelled') || 
          error?.message?.includes('cancelled') ||
          error?.message?.includes('Activity') ||
          error?.message?.includes('No Image') ||
          error?.code === 'RESULT_CANCELED' ||
          error?.code === 'USER_CANCELLED') {
        console.log('Camera cancelled by user');
        return null;
      }
      console.error('Error taking photo:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [isNative, quality, maxWidth, maxHeight]);

  const pickFromGallery = useCallback(async (): Promise<File | null> => {
    if (!isNative) {
      console.warn('pickFromGallery is only available on native platforms');
      return null;
    }

    setLoading(true);
    try {
      const photo = await Camera.getPhoto({
        quality,
        allowEditing: false,
        resultType: CameraResultType.Base64,
        source: CameraSource.Photos,
        width: maxWidth,
        height: maxHeight,
        correctOrientation: true,
      });

      return await convertPhotoToFile(photo);
    } catch (error: any) {
      // User cancelled - don't throw (handles iOS and Android)
      if (error?.message?.includes('User cancelled') || 
          error?.message?.includes('cancelled') ||
          error?.message?.includes('Activity') ||
          error?.message?.includes('No Image') ||
          error?.code === 'RESULT_CANCELED' ||
          error?.code === 'USER_CANCELLED') {
        console.log('Gallery selection cancelled by user');
        return null;
      }
      console.error('Error picking from gallery:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [isNative, quality, maxWidth, maxHeight]);

  const pickImage = useCallback(async (): Promise<File | null> => {
    if (!isNative) {
      console.warn('pickImage is only available on native platforms');
      return null;
    }

    setLoading(true);
    try {
      // Let user choose between camera and gallery
      const photo = await Camera.getPhoto({
        quality,
        allowEditing: false,
        resultType: CameraResultType.Base64,
        source: CameraSource.Prompt, // Shows action sheet with Camera/Gallery options
        width: maxWidth,
        height: maxHeight,
        correctOrientation: true,
        promptLabelHeader: 'Select Image',
        promptLabelPhoto: 'Choose from Library',
        promptLabelPicture: 'Take Photo',
      });

      return await convertPhotoToFile(photo);
    } catch (error: any) {
      // User cancelled - don't throw (handles iOS and Android)
      if (error?.message?.includes('User cancelled') || 
          error?.message?.includes('cancelled') ||
          error?.message?.includes('Activity') ||
          error?.message?.includes('No Image') ||
          error?.code === 'RESULT_CANCELED' ||
          error?.code === 'USER_CANCELLED') {
        console.log('Image selection cancelled by user');
        return null;
      }
      console.error('Error picking image:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [isNative, quality, maxWidth, maxHeight]);

  const checkPermissions = useCallback(async () => {
    if (!isNative) return { camera: 'granted', photos: 'granted' };
    
    try {
      const permissions = await Camera.checkPermissions();
      return permissions;
    } catch (error) {
      console.error('Error checking camera permissions:', error);
      return { camera: 'denied', photos: 'denied' };
    }
  }, [isNative]);

  const requestPermissions = useCallback(async () => {
    if (!isNative) return { camera: 'granted', photos: 'granted' };
    
    try {
      const permissions = await Camera.requestPermissions();
      return permissions;
    } catch (error) {
      console.error('Error requesting camera permissions:', error);
      return { camera: 'denied', photos: 'denied' };
    }
  }, [isNative]);

  return {
    isNative,
    loading,
    takePhoto,
    pickFromGallery,
    pickImage,
    checkPermissions,
    requestPermissions,
  };
};
